import _ from 'lodash';
import { sqExploreFolderModalStore, sqHomeScreenStore } from '@/core/core.stores';
import { flux } from '@/core/flux.module';
import { tabFolders } from '@/homescreen/homescreen.constants';
import { SEARCH_ITEM_LOCATIONS } from '@/main/app.constants';
import { cancelGroup } from '@/requests/pendingRequests.utilities';
import { sqFoldersApi } from '@/sdk/api/FoldersApi';
import { isAdmin } from '@/services/authorization.service';
import { isEveryoneGroupEnabled, isExternalOrganizationTabEnabled } from '@/services/systemConfiguration.utilities';
import { getTabFolder } from '@/utilities/homescreen.utilities';
import { formatNestedFolderWithAncestors, getTabFolderName } from '@/utilities/homescreen.utilities';
import { equalsIgnoreCase } from '@/utilities/utilities';
import type { Folder, InitialFolderParams } from '@/explorer/exploreModal.types';
import type { SearchItemLocation } from '@/main/app.constants';

export const exportForTesting = {
  setInitialFolderId,
  setWorkbookId,
  setSort,
  clear,
  setTableFilter,
  setPageSizeForTable,
  setPageNumberAndGo,
  setSearchParams,
  loadItems,
  setFolder,
  setShowResults,
  getInitialFolder,
};

/**
 * Sets the ID of the folder where the user started
 *
 * @param initialFolderId - ID of a folder
 */
export function setInitialFolderId(initialFolderId?: string) {
  flux.dispatch('EXPLORE_FOLDER_MODAL_SET_INITIAL_FOLDER_ID', {
    initialFolderId,
  });
}

/**
 * Sets the workbook ID
 *
 * @param workbookId - a workbook ID
 */
export function setWorkbookId(workbookId?: string) {
  flux.dispatch('EXPLORE_FOLDER_MODAL_SET_WORKBOOK_ID', { workbookId });
}

/**
 * Sets the sort field that controls the sorting of the workbooks and toggles the direction if the field
 * was already set to the specified one.
 *
 * @param sortProperty - One of WORKBOOKS_SORT_FIELDS
 * @param sortAscending - True for asc, false for desc
 * @param forceSortDirection=false - True if the given sort direction must not be flipped when sorting
 *   on the currently sorted property (normally used to toggle sort direction)
 */
export function setSort(sortProperty: string, ascending: boolean, forceSortDirection = false) {
  const sortAscending =
    !forceSortDirection && sortProperty === sqExploreFolderModalStore.sortProperty ? !ascending : ascending;
  flux.dispatch('EXPLORE_FOLDER_MODAL_SET_SORT', {
    sortProperty,
    sortAscending,
  });
}

/**
 * Clears the data for the currently loaded seeq content image.
 */
export function clear() {
  flux.dispatch('EXPLORE_FOLDER_MODAL_CLEAR');
}

export function setTableFilter(tableFilter: SearchItemLocation) {
  flux.dispatch('EXPLORE_FOLDER_MODAL_SET_TABLE_FILTER', { tableFilter });
}

export function setSearchParams(field: string, value: string) {
  flux.dispatch('EXPLORE_FOLDER_MODAL_SET_SEARCH_PARAMS', { field, value });
}

export function setPageSizeForTable({ size }: { size: number }) {
  flux.dispatch('EXPLORE_FOLDER_MODAL_SET_PAGE_SIZE', { pageSize: size });
}

export function setPageNumberAndGo(page: number, loadTable: (...args: any[]) => any) {
  flux.dispatch('EXPLORE_FOLDER_MODAL_SET_PAGE_NUMBER', { pageNumber: page });
  loadTable();
}

export function setFolder(folder?: Folder) {
  flux.dispatch('EXPLORE_FOLDER_MODAL_SET_FOLDER', { folder });
}

export function setShowResults(showResults: boolean) {
  flux.dispatch('EXPLORE_FOLDER_MODAL_SET_SHOW_RESULTS', { showResults });
}

export function setTableRoot(root?: string) {
  flux.dispatch('EXPLORE_FOLDER_MODAL_SET_TABLE_ROOT', { root });
}

export function loadItems() {
  if (_.isUndefined(sqExploreFolderModalStore.initialFolderId)) {
    return;
  }

  const limit = sqExploreFolderModalStore.getPageSizeByTable();
  const currentPageNumber = sqExploreFolderModalStore.getPageNumberForTable();
  const offset = (currentPageNumber - 1) * limit;
  const sortDirection = sqExploreFolderModalStore.sortAscending ? 'asc' : 'desc';
  const sortOrder = `${sqExploreFolderModalStore.sortProperty} ${sortDirection}`;
  const root = sqExploreFolderModalStore.tableRoot;
  const filter = sqExploreFolderModalStore.searchParams.filter ?? root;
  const params = _.assign({}, sqExploreFolderModalStore.searchParams, {
    limit,
    offset,
    sortOrder,
    root,
    filter:
      sqExploreFolderModalStore.initialFolderId === null && !sqExploreFolderModalStore.searchParams.textSearch
        ? SEARCH_ITEM_LOCATIONS.SHARED_OR_PUBLIC
        : filter,
  });

  const cancellationGroup = 'exploreFolderModalLoadItems';
  flux.dispatch('EXPLORE_FOLDER_MODAL_SET_LOADING', { loading: true });
  cancelGroup(cancellationGroup);
  return sqFoldersApi
    .getFolders(params, { cancellationGroup })
    .then((response) => {
      flux.dispatch('EXPLORE_FOLDER_MODAL_SET_ITEMS', {
        items: response.data?.content,
      });
      flux.dispatch('EXPLORE_FOLDER_MODAL_SET_ITEM_TOTAL_FOR_TABLE', {
        itemTotalForTable: response.data?.totalResults,
      });
    })
    .finally(() => flux.dispatch('EXPLORE_FOLDER_MODAL_SET_LOADING', { loading: false }));
}

export function getRootAndUpdateFilter(folderId: string) {
  if (folderId) {
    return sqFoldersApi.getAncestors({ folderId }).then((folderTree) => {
      const itemFolderRoot = folderTree.data.id;
      if (
        _.includes(
          [SEARCH_ITEM_LOCATIONS.CORPORATE, SEARCH_ITEM_LOCATIONS.SHARED_OR_PUBLIC, SEARCH_ITEM_LOCATIONS.MY_FOLDER],
          itemFolderRoot,
        )
      ) {
        exportForTesting.setTableFilter(itemFolderRoot as SearchItemLocation);
        exportForTesting.setSearchParams('filter', itemFolderRoot);
      }
    });
  } else {
    return Promise.resolve({});
  }
}

export function getParentFolderId(currentTab: string, item: any) {
  sqFoldersApi.getAncestors({ folderId: item?.id }).then((result) => {
    const locationValue = item.name === 'Corporate folder' ? SEARCH_ITEM_LOCATIONS.CORPORATE : result.data.id;
    getInitialFolder({
      currentTab,
      item,
      parentFolderId: result.data.id,
      locationValue,
      currentFolderId: item?.id,
    });
  });
}

/**
 * @param {Boolean} forceCorporate - checks if the function is being called within the corporate drive message,
 * which will default the folder to being the corporate drive
 */
export function getInitialFolder({
  currentTab,
  item,
  forceCorporate = false,
  insideAdvancedSearch = false,
  isCurrentFolderNested = false,
  breadcrumbsPresent = false,
  breadcrumbClicked = false,
  locationChangedInAdvanced = false,
  currentFolderId = '',
  locationValue = '',
  parentFolderId = '',
}: InitialFolderParams) {
  let tabFolderPromise;
  let tableFilter = currentTab;
  const tabFolder = _.includes(
    tabFolders({
      isAdmin: isAdmin(),
      includeCorporate: isEveryoneGroupEnabled(),
      includeExternallyShared: isExternalOrganizationTabEnabled(),
    }),
    currentTab,
  )
    ? currentTab
    : undefined;

  getTabFolder(tabFolder).then((folder: Folder) => {
    let updatedFolder = _.cloneDeep(folder);
    if (
      (item !== undefined && !forceCorporate && insideAdvancedSearch) ||
      (item && item?.parentFolderId && !equalsIgnoreCase(item.parentFolderId, updatedFolder?.id) && !forceCorporate)
    ) {
      tabFolderPromise = getTabFolderName(item.ancestors[0]?.id);
      tabFolderPromise.then((tabName) => {
        tableFilter = item.ancestors[0]?.id ? (tabName ?? '') : tableFilter;
        updatedFolder = insideAdvancedSearch
          ? item
          : _.assign({}, _.last(item.ancestors), {
              ancestors: _.slice(item.ancestors, 0, item.ancestors.length - 1),
            });
      });
    } else {
      tabFolderPromise = Promise.resolve();
    }

    // If the user is inside a nested folder and they are opening the specific folder modal within the home
    // screen advanced search, then the initial folder needs to be set to the folder of the current folder id.
    // When a user changes the location of the search or clicks on a breadcrumb, the modal should no longer show
    // the contents of the current folder id, so this condition will be skipped over.
    if (
      (isCurrentFolderNested &&
        insideAdvancedSearch &&
        breadcrumbsPresent &&
        !breadcrumbClicked &&
        !locationChangedInAdvanced &&
        currentFolderId &&
        !item) ||
      parentFolderId
    ) {
      return tabFolderPromise.then(() => {
        formatNestedFolderWithAncestors(
          currentFolderId ?? sqHomeScreenStore.currentFolderId,
          locationValue,
          false,
        ).then((response) => {
          let filter: string = parentFolderId;
          if (response?.name === 'Corporate folder') {
            filter = SEARCH_ITEM_LOCATIONS.CORPORATE;
          } else if (response?.name === 'Users folder') {
            filter = SEARCH_ITEM_LOCATIONS.USERS;
          }

          exportForTesting.setFolder(response);
          exportForTesting.setTableFilter(
            (filter as SearchItemLocation) ?? SEARCH_ITEM_LOCATIONS[tableFilter as keyof typeof SEARCH_ITEM_LOCATIONS],
          );
          exportForTesting.setSearchParams('folderId', response?.id);
          exportForTesting.setInitialFolderId(response?.id);
        });
      });
    } else {
      const advancedSearchId = item ? item?.id : updatedFolder?.id;
      const folderId = item && !forceCorporate && !insideAdvancedSearch ? item?.parentFolderId : advancedSearchId;
      return tabFolderPromise.then(() => {
        exportForTesting.setFolder(updatedFolder);
        exportForTesting.setTableFilter(SEARCH_ITEM_LOCATIONS[tableFilter as keyof typeof SEARCH_ITEM_LOCATIONS]);
        exportForTesting.setSearchParams('folderId', folderId);
        exportForTesting.setInitialFolderId(folderId);
      });
    }
  });
}
